import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CiMedicalCross } from "react-icons/ci";
import { PiExportBold, PiWarningCircleLight } from "react-icons/pi";
import { CiEdit } from "react-icons/ci";
import { BiSolidTrash } from "react-icons/bi";
import { LoadingOutlined } from "@ant-design/icons";
import { Input, message, Select, Spin, Tooltip } from "antd";


import "./styles.scss";
import uris from "../../../config/uris";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import CustomTable from "../../../assets/component/table";
import CustomModal from "../../../assets/component/modal";
import {
  exportEntities,
  exportEntitiesReset,
  retrieveEntities,
  retrieveEntitiesReset,
} from "../../../store/slices/entities/retrieveEntities";
import { manageEntitiesFilter } from "../../../store/slices/entities/entitiesFilters";
import {
  deleteEntities,
  deleteEntitiesReset,
  editEntities,
  editEntitiesReset,
} from "../../../store/slices/entities/updateEntities";
import { decryptData } from "../../../helpers/encrypt/crypto-encrypt";

const EditModal = ({
  handleClose,
  country,
  setCountry,
  countryCode,
  setCountryCode,
  entityName,
  setEntityName,
  investorName,
  setInvestorName,
  industry,
  setIndustry,
  region,
  setRegion,
  securityId,
  setSecurityId,
  types,
  setTypes,
  targetCompanyId,
  setTargetCompanyId,
  matchingId,
  setMatchingId,
  editEntitiesLoading,
  selectedObject,
  antIcon,
  dispatch,
}) => {
  const [formErrors, setFormErrors] = useState({
    country: "",
    countryCode: "",
    entityName: "",
    industry: "",
    region: "",
    securityId: "",
    types: "",
    targetCompanyId: "",
    matchingId: "",
  });
  const renderField = (
    label,
    placeholder,
    value,
    onChange,
    additionalContent,
    isMandatory = true
  ) => (
    <>
      <div className="cell-content">
        <div className="modal-body-title">{label} {isMandatory && <span className="mandotory-icon">*</span>}</div>
        <div className="modal-body-content">
          <Input
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>

        {additionalContent && (
          <div className="additional-content">{additionalContent}</div>
        )}
      </div>

    </>
  );

  const addMatchingField = () => {
    setMatchingId((prevIds) => {
      if (prevIds?.at(-1)?.trim() === "") {
        return prevIds;
      }
      let updatedIds = [...prevIds, ""];
      return updatedIds;
    });
  };

  const handleMatchingChange = (index, value) => {
    const updatedMatchingId = [...matchingId];
    updatedMatchingId[index] = value;
    setMatchingId(updatedMatchingId);
  };

  const removeMatchingField = (index) => {
    const updatedMatchingId = matchingId.filter((_, i) => i !== index);
    setMatchingId(updatedMatchingId);
  };

  const addTargetCompanyField = () => {
    setTargetCompanyId((prevIds) => {
      if (prevIds?.at(-1)?.trim() === "") {
        return prevIds;
      }
      let updatedIds = [...prevIds, ""];
      return updatedIds;
    });
  };

  const handleTargetCompanyChange = (index, value) => {
    setTargetCompanyId((prevIds) => {
      const updatedIds = [...prevIds];
      updatedIds[index] = value;
      return updatedIds.every(id => id.trim() === "") ? [] : updatedIds;
    });
  };

  const removeTargetCompanyField = (index) => {
    const updatedTargetCompanyIds = targetCompanyId.filter((_, i) => i !== index);
    setTargetCompanyId(updatedTargetCompanyIds);
  };

  const handleSubmit = () => {
    let errors = { matchingId: [], targetCompanyId: [] };
    if (!country) {
      errors.country = "*Please enter the country";
    }
    if (!countryCode) {
      errors.countryCode = "*Please enter the country code";
    }
    if (!entityName) {
      errors.entityName = "*Please enter the entity name";
    }
    if (!industry) {
      errors.industry = "*Please enter the industry";
    }
    if (!region) {
      errors.region = "*Please enter the region";
    }
    if (!securityId) {
      errors.securityId = "*Please enter the securityId";
    }
    if (!types) {
      errors.types = "*Please enter the types";
    }

    if (Array.isArray(targetCompanyId)) {
      targetCompanyId.forEach((id, index) => {
        if (id && !/^[a-fA-F0-9]{24}$/.test(id)) {
          errors.targetCompanyId[index] = "*Invalid ID";
        }
      });
    }

    if (Array.isArray(matchingId)) {
      matchingId.forEach((id, index) => {
        if (!id) {
          errors.matchingId[index] = "*Please enter the matching ID";
        } else if (!/^[a-fA-F0-9]{24}$/.test(id)) {
          errors.matchingId[index] = "*Invalid ID";
        }
      });
    }
    if (!errors.targetCompanyId.some((error) => error)) {
      delete errors.targetCompanyId;
    }

    if (!errors.matchingId.some((error) => error)) {
      delete errors.matchingId;
    }

    setFormErrors(errors);

    if (Object.keys(errors)?.length === 0) {
      const cleanedTargetCompanyId = targetCompanyId.filter(id => id.trim() !== "");
      const payload = {
        entityId: selectedObject._id,
        country: country,
        countryCode: countryCode,
        entityName: entityName,
        investorName: investorName,
        industry: industry,
        region: region,
        securityId: securityId,
        types: types?.split(","),
        targetCompanyId: cleanedTargetCompanyId,
        matchingIds: matchingId,
      };
      dispatch(editEntities(payload));
    }
  };

  return (
    <>
      <div className="row-content">
        {renderField(
          "Country",
          "Enter the country",
          country,
          setCountry,
          formErrors.country && (
            <div className="error-message">{formErrors.country}</div>
          )
        )}
        {renderField(
          "Country Code",
          "Enter the country code",
          countryCode,
          setCountryCode,
          formErrors.countryCode && (
            <div className="error-message">{formErrors.countryCode}</div>
          )
        )}
      </div>
      <div className="row-content">
        {renderField(
          "Entity Name",
          "Enter the entity name",
          entityName,
          setEntityName,
          formErrors.entityName && (
            <div className="error-message">{formErrors.entityName}</div>
          )
        )}
        {renderField(
          "Investor Name",
          "Enter the investor name",
          investorName,
          setInvestorName,
          null,
          false,
        )}

      </div>
      <div className="row-content">
        {renderField(
          "Industry",
          "Enter the industry",
          industry,
          setIndustry,
          formErrors.industry && (
            <div className="error-message">{formErrors.industry}</div>
          )
        )}
        {renderField(
          "Region",
          "Enter the region",
          region,
          setRegion,
          formErrors.region && (
            <div className="error-message">{formErrors.region}</div>
          )
        )}

      </div>
      <div className="row-content">
        {renderField(
          "Security Id",
          "Enter the securityId",
          securityId,
          setSecurityId,
          formErrors.securityId && (
            <div className="error-message">{formErrors.securityId}</div>
          )
        )}
        {renderField(
          "Types",
          "Enter the types",
          types,
          setTypes,
          formErrors.types && (
            <div className="error-message">{formErrors?.types}</div>
          )
        )}
      </div>

      <div className="row-content">
        <div className="cell-content">
          <div className="modal-body-title">Target Company Id</div>
          <div className="targetCompanyId-field">
            {(targetCompanyId?.length === 0 ? [""] : targetCompanyId)?.map((id, index) => (
              <div key={index} className="target-field">
                <div className="target-field-data">
                  <Input
                    placeholder="Enter the target company Id"
                    value={id}
                    onChange={(e) => handleTargetCompanyChange(index, e.target.value)}
                  />
                  {index === 0 ? (
                    <div className="addIcon" onClick={addTargetCompanyField}>
                      <Tooltip title="Add"><span className="action-addIcon"><CiMedicalCross /></span></Tooltip>
                    </div>
                  ) : (
                    <div className="delete-icon" onClick={() => removeTargetCompanyField(index)}>
                      <Tooltip><span className="action-delete-icon"><BiSolidTrash /></span></Tooltip>
                    </div>
                  )}
                </div>
                {formErrors?.targetCompanyId?.[index] && (
                  <div className="error-message">{formErrors.targetCompanyId[index]}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="cell-content">
          <div className="modal-body-title">Source Company Id <span className="mandotory-icon">*</span></div>
          <div className="matchingId-field">
            {(matchingId?.length === 0 ? [""] : matchingId)?.map((id, index) => (
              <div key={index} className="matching-field">
                <div className="matching-field-data">
                  <Input
                    placeholder={`Enter the source company Id`}
                    value={id}
                    onChange={(e) => handleMatchingChange(index, e.target.value)}
                  />

                  {index === 0 && (
                    <div className="addIcon" onClick={addMatchingField}>
                      <Tooltip title="Add">
                        <span className="action-addIcon">
                          <CiMedicalCross />
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  {index !== 0 && (
                    <div
                      className="delete-icon"
                      onClick={() => removeMatchingField(index)}
                    >
                      <Tooltip>
                        <span className="action-delete-icon">
                          <BiSolidTrash />
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
                {formErrors?.matchingId?.[index] && (
                  <div className="error-message">{formErrors.matchingId[index]}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <div
          className="button-wrapper submit"
          onClick={() => handleSubmit(selectedObject?._id)}
        >
          {editEntitiesLoading ? <Spin className="submit-loading" size="small" indicator={antIcon} /> : "Submit"}
        </div>
        <div className="button-wrapper cancel" onClick={handleClose}>
          Cancel
        </div>
      </div>
    </>
  );
};

const DeleteModalTitle = () => {
  return (
    <>
      <div className="modal-icon">
        <PiWarningCircleLight />
      </div>
      <div className="modal-title">Confirm</div>
    </>
  );
};

const DeleteModal = ({ message, handleDelete, handleClose, deleteEntitiesLoading, antIcon }) => {

  return (
    <>
      <p className="modal">{message}</p>
      <div className="action-buttons">
        <div className="button-wrapper yes" onClick={handleDelete}>
          {deleteEntitiesLoading ? <Spin size="small" indicator={antIcon} /> : "Yes"}
        </div>
        <div className="button-wrapper no" onClick={handleClose}>
          No
        </div>
      </div>
    </>
  );
};

const ManageEntity = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [countries, setCountries] = useState([]);
  const [entities, setEntities] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [country, setCountry] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [entityName, setEntityName] = useState("");
  const [investorName, setInvestorName] = useState("");
  const [industry, setIndustry] = useState("");
  const [region, setRegion] = useState("");
  const [securityId, setSecurityId] = useState("");
  const [types, setTypes] = useState("");
  const [targetCompanyId, setTargetCompanyId] = useState([""]);
  const [matchingId, setMatchingId] = useState([""]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [errors, setErrors] = useState({
    country: "",
    entity: "",
  });
  const [formattedTableData, setFormattedTableData] = useState({
    dataSource: [],
    columns: [],
  });

  const dispatch = useDispatch();
  const role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))
  const { data: manageEntitiesFilterData } = useSelector((state) => state?.manageEntitiesFilter);
  const { data: retrieveEntitiesData, isLoading: retrieveEntitiesLoading, error: retrieveEntitiesError } = useSelector((state) => state?.retrieveEntities);
  const { success: exportEntitiesSuccess, isLoading: exportEntitiesLoading, error: exportEntitiesError } = useSelector((state) => state?.exportEntities);
  const { data: editEntitiesData, isLoading: editEntitiesLoading, success: editEntitiesSuccess, error: editEntitiesError } = useSelector((state) => state?.editEntities)
  const { data: deleteEntitiesData, success: deleteEntitiesSuccess, isLoading: deleteEntitiesLoading, error: deleteEntitiesError } = useSelector((state) => state?.deleteEntities);

  const menuData = {
    title: "Entities",
    parentMenu: {
      name: "Entities",
      link: uris?.uploadEntities,
    },
    subMenu: {
      name: "Manage Entities",
      link: uris?.manageEntity,
    },
  };

  const tableHeaderData = [
    {
      title: "Entity Name",
      dataIndex: "entityName",
      key: "entityName",
      width: "25%",
      sorter: {
        compare: (a, b) => a.entityName.localeCompare(b.entityName),
        multiple: 3,
      },
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: "25%",
      sorter: {
        compare: (a, b) => a.region.localeCompare(b.region),
        multiple: 3,
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: "25%",
      sorter: {
        compare: (a, b) => a.country - b.country,
        multiple: 3,
      },
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
      width: "25%",
      sorter: {
        compare: (a, b) => a.industry - b.industry,
        multiple: 3,
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "25%",
    },
  ];

  const tableStructuredData = () => {
    const formattedData = retrieveEntitiesData?.data?.map((entity) => {
      return {
        key: entity._id ? entity._id : "-",
        entityName: entity.entityName ? entity.entityName : "-",
        region: entity.region ? entity.region : "-",
        country: entity.country ? entity.country : "-",
        industry: entity.industry ? entity.industry : "-",
        actions: (
          <div className="action-icons-container">
            <div className="edit-icon" onClick={() => handleEdit(entity)}>
              <Tooltip title="Edit">
                <span className="action-edit-icon">
                  <CiEdit />
                </span>
              </Tooltip>
            </div>
            {["admin"]?.includes((role_decrypt?.role || "")) && (
              <div className="delete-icon" onClick={() => handleDelete(entity)}>
                <Tooltip title="Delete">
                  <span className="action-delete-icon">
                    <BiSolidTrash />
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        ),
      };
    });
    setFormattedTableData({
      ...formattedTableData,
      columns: tableHeaderData,
      dataSource: formattedData,
    });
  };

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 13, color: "white" }} spin />
  );

  const isApplyDisabled = selectedCountry === null && selectedEntity === null;

  const onApply = () => {
    if (!selectedCountry && !selectedEntity) {

      return;
    } else {
      const payload = {
        country: selectedCountry,
        entityId: selectedEntity,
        isFilterApplied: true,
        page: page,
        limit: limit,
      };

      dispatch(retrieveEntities(payload));
    }
  };

  const onReset = () => {
    setSelectedCountry(null);
    setSelectedEntity(null);
    setEntities([])
  };

  const handleExportMasterEntities = () => {
    const payload = {
      country: selectedCountry,
      entityId: selectedEntity,
    };
    dispatch(exportEntities(payload));
  };
    
  const handleEdit = (value) => {
    
    if (value) {
      setSelectedObject(value);
      setCountry(value.country);
      setCountryCode(value.countryCode);
      setEntityName(value.entityName);
      setInvestorName(value.investorName);
      setIndustry(value.industry);
      setRegion(value.region);
      setSecurityId(value.securityId);
      setTypes(value?.types?.join(","));
      setTargetCompanyId(value.targetCompanyId);
      setMatchingId(value.matchingIds);
      setShowEditModal(true);
    }
  };

  const handleDelete = (value) => {
    if (value) {
      setSelectedObject(value);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = (value) => {
    const payload = {
      entityId: value,
    };
    dispatch(deleteEntities(payload));
  };

  const onCountryChange = (value) => {
    if (value) {
      setSelectedCountry(value);
      setSelectedEntity(null);
      setEntities([])
      setErrors((prevValues) => ({
        ...prevValues,
        country: "",
      }));
    } else {
      setSelectedCountry(null);
      setSelectedEntity(null);
      setEntities([])
    }
  };

  const onEntityChange = (value) => {
    setSelectedEntity(value ? value : null);
    setErrors((prevValues) => ({
      ...prevValues,
      entity: "",
    }));
  };

  const onEntitySearch = (value) => {
    if (value?.length >= 3) {
      setIsFiltering(true);
      const payload = {
        country: selectedCountry,
        searchValue: value,
      };
      dispatch(manageEntitiesFilter(payload));
    } else {
      setEntities([]);
    }
  };

  const payload = {
    isFilterApplied: true,
    page: page,
    limit: limit,
  };

  useEffect(() => {
    setIsFiltering(false);
    dispatch(manageEntitiesFilter({}));
  }, []);

  useEffect(()=>{
    onApply();
  },[page,limit])

  useEffect(() => {
    if (isApplyDisabled) {
      dispatch(retrieveEntities(payload))
    }
  }, [isApplyDisabled, limit, page])

  useEffect(() => {
    tableStructuredData();
  }, [retrieveEntitiesData, deleteEntitiesSuccess]);

  useEffect(() => {
    if (manageEntitiesFilterData) {
      if (!isFiltering) {
        setCountries(manageEntitiesFilterData?.data);
      } else {
        setEntities(manageEntitiesFilterData?.data);
      }
    }
  }, [manageEntitiesFilterData, isFiltering]);

  useEffect(() => {
    if (exportEntitiesSuccess) {
      message.success("Entities Retrieved succcessfully");
      dispatch(exportEntitiesReset());
    }
    if (exportEntitiesError) {
      message.error(exportEntitiesError.message);
      dispatch(exportEntitiesReset());
    }
    if (retrieveEntitiesError) {
      message.error(retrieveEntitiesError.message);
      dispatch(retrieveEntitiesReset());
    }
  }, [exportEntitiesSuccess, exportEntitiesError, retrieveEntitiesError]);

  useEffect(() => {
    if (editEntitiesSuccess) {
      message.success(editEntitiesData?.message)
      setShowEditModal(false);
      if (selectedCountry || selectedEntity) {
        onApply();
      } else {
        dispatch(retrieveEntities(payload))
      }
      dispatch(editEntitiesReset())
    }
    if (editEntitiesError) {
      message.error(editEntitiesError?.message)
      dispatch(editEntitiesReset())
    }
  }, [editEntitiesSuccess, editEntitiesError])

  useEffect(() => {
    if (deleteEntitiesSuccess) {
      setShowDeleteModal(false)
      message.success(deleteEntitiesData?.message);
      dispatch(deleteEntitiesReset());
      onReset();
    }
    if (deleteEntitiesError) {
      message.error(deleteEntitiesError?.message);
      dispatch(deleteEntitiesReset());
    }
  }, [deleteEntitiesSuccess, deleteEntitiesError]);

  return (
    <div className="manage-entity-container">
      <Breadcrumbs breadcrumbDetails={menuData} />
      <div className="show-filters-section">
        <div className="manage-entity-filters-section">
          <div className="input-wrapper">
            <div className="input-label">
              Country
            </div>
            <div className="input-container">
              <Select
                allowClear
                showSearch
                className="country-select"
                options={countries}
                onChange={onCountryChange}
                value={selectedCountry}
                placeholder="Select Country"
                optionFilterProp="label"
              />
            </div>
            {<div className="error-messages">{errors?.country}</div>}
          </div>

          <div className="input-wrapper">
            <div className="input-label">
              Entity Name
            </div>
            <div className="input-container">
              <Select
                allowClear
                showSearch
                className="entity-select"
                options={entities}
                onChange={onEntityChange}
                onSearch={onEntitySearch}
                value={selectedEntity}
                placeholder="Select Entity"
                filterOption={false}
              />
            </div>
            {<div className="error-messages">{errors?.entity}</div>}
          </div>

          <div className="filters-button-section">
            <div
              className={`${isApplyDisabled ? "button-wrapper disabled" : "button-wrapper apply-filter"}`}
              onClick={!isApplyDisabled ? () => onApply() : null}
            >
              Apply
            </div>
            <div
              className="button-wrapper reset-filter"
              onClick={() => onReset()}
            >
              Reset
            </div>
          </div>
          <div
            className="export-button-section"
          >
            <div className="button-wrapper export" onClick={handleExportMasterEntities}>
              {exportEntitiesLoading ? (
                <Spin className="spin-loader" indicator={antIcon} />
              ) : (
                <PiExportBold />
              )}
              Export
            </div>
          </div>
        </div>
      </div>
      <div className="entity-list-table-section">
        <CustomTable
          page={page}
          limit={limit}
          setPage={setPage}
          setLimit={setLimit}
          tableData={formattedTableData}
          isLoading={retrieveEntitiesLoading}
          totalRows={retrieveEntitiesData?.totalEntitiesCount}
        />
      </div>

      {showEditModal && (
        <CustomModal
          modalTitle="Edit Entity"
          showCloseIcon
          handleClose={() => setShowEditModal(false)}
        >
          <EditModal
            className="edit-entity-modal"
            country={country}
            setCountry={setCountry}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            entityName={entityName}
            setEntityName={setEntityName}
            investorName={investorName}
            setInvestorName={setInvestorName}
            industry={industry}
            setIndustry={setIndustry}
            region={region}
            setRegion={setRegion}
            securityId={securityId}
            types={types}
            setTypes={setTypes}
            setSecurityId={setSecurityId}
            targetCompanyId={targetCompanyId}
            setTargetCompanyId={setTargetCompanyId}
            matchingId={matchingId}
            setMatchingId={setMatchingId}
            handleClose={() => setShowEditModal(false)}
            selectedObject={selectedObject}
            editEntitiesLoading={editEntitiesLoading}
            antIcon={antIcon}
            dispatch={dispatch}
          ></EditModal>
        </CustomModal>
      )}
      {showDeleteModal && (
        <CustomModal
          modalTitle={<DeleteModalTitle />}
          showCloseIcon
          handleClose={() => setShowDeleteModal(false)}
        >
          <DeleteModal
            deleteEntitiesLoading={deleteEntitiesLoading}
            message={
              <>
                Are you sure you want to delete{" "}
                <strong>{selectedObject.entityName}</strong>?
              </>
            }
            handleDelete={() => handleConfirmDelete(selectedObject._id)}
            handleClose={() => setShowDeleteModal(false)}
            antIcon={antIcon}
          ></DeleteModal>
        </CustomModal>
      )}
    </div>
  );
};

export default ManageEntity;
